import {
	LuIcon,
	Menu,
	type MenuClickTriggerProps,
	type MenuRootProps,
	type MenuVariantProps,
	stopPropagation,
} from "@overmap-ai/blocks"
import type { AssetTypeStatus, Stored } from "@overmap-ai/common"
import { memo, type ReactElement } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { AssetTypeStatusGroup } from "../MenuGroups/AssetTypeStatusGroup"

enum ProcedureBulkDropdownMenuPages {
	INITIAL = "initial",
	STATUS = "status",
}

interface ProcedureBulkDropdownMenuProps
	extends MenuRootProps, Omit<MenuClickTriggerProps, "children">, MenuVariantProps {
	children: ReactElement
	onExportCsv?: () => void
	onExportPdf?: () => void
	onLinkIssue?: () => void
	onDelete?: () => void
	statuses?: Stored<AssetTypeStatus>[]
	onStatusSelect?: (status: Stored<AssetTypeStatus>) => void
}

export const ProcedureBulkDropdownMenu = memo((props: ProcedureBulkDropdownMenuProps) => {
	const {
		children,
		disabled,
		size,
		variant,
		radius,
		accentColor,
		onExportPdf,
		onExportCsv,
		onLinkIssue,
		onDelete,
		statuses,
		onStatusSelect,
		...rest
	} = props

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content
				size={size}
				variant={variant}
				accentColor={accentColor}
				radius={radius}
				onClick={stopPropagation}
			>
				<Menu.Scroll>
					<Menu.Pages defaultPage={ProcedureBulkDropdownMenuPages.INITIAL}>
						<Menu.PageContent page={ProcedureBulkDropdownMenuPages.INITIAL}>
							{!!statuses && !!onStatusSelect ? (
								<Menu.PageTrigger page={ProcedureBulkDropdownMenuPages.STATUS}>
									<LuIcon icon={modelIconMapping.status} />
									Change status
								</Menu.PageTrigger>
							) : null}

							{onLinkIssue ? (
								<Menu.Item onSelect={onLinkIssue}>
									<LuIcon icon={modelIconMapping.issue} />
									Link issue
								</Menu.Item>
							) : null}

							{!!onExportPdf || !!onExportCsv ? (
								<>
									{onExportPdf ? (
										<Menu.Item onSelect={onExportPdf}>
											<LuIcon icon={modelIconMapping.download} />
											Export PDF
										</Menu.Item>
									) : null}
									{onExportCsv ? (
										<Menu.Item onSelect={onExportCsv}>
											<LuIcon icon={modelIconMapping.download} />
											Export CSV
										</Menu.Item>
									) : null}
								</>
							) : null}

							{onDelete ? (
								<>
									<Menu.Separator />
									<Menu.Item onSelect={onDelete}>
										<LuIcon icon={modelIconMapping.delete} />
										Delete
									</Menu.Item>
								</>
							) : null}
						</Menu.PageContent>

						{!!statuses && !!onStatusSelect ? (
							<Menu.PageContent page={ProcedureBulkDropdownMenuPages.STATUS}>
								<AssetTypeStatusGroup
									assetTypeStatuses={statuses}
									onSelectAssetTypeStatus={onStatusSelect}
								/>
							</Menu.PageContent>
						) : null}
					</Menu.Pages>
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
ProcedureBulkDropdownMenu.displayName = "ProcedureInitiativeBulkDropdownMenu"
